import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
    Box,
    ButtonBase,
    Modal,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import AddRounded from "@mui/icons-material/AddRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import { useCookies } from "react-cookie";
import Category from "../../../api/Category";
import Service from "../../../api/Service";
import BottomButton from "../../BottomButton";
import { useNavigate } from "react-router-dom";
import SectionView from "../../SectionView";
import DeleteDialog from "../../Modals/DeleteDialog";
import ErrorDialog from "../../ErrorDialog";
import { getPreferredLanguage } from "../../../utils/languageUtils";
import TagFilterBar from "../../TagFilterBar";
import EmptyListState from "../../EmptyListState";
import Brand from "../../../api/Brand";
import Footer from "../Footer";
import ConfirmModal from "../../ConfirmModal";
import useCurrentLanguage from "../../../hooks/useCurrentLanguage";
import { pickLocalized } from "../../../utils/localize";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import { FONTS, CARD_STYLES } from "../../../constants/styles";
import {
    DndContext,
    DragOverlay,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragStartEvent,
    DragOverEvent,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const DEFAULT_CATEGORY = { name: "Section", enabled: true };
const DEFAULT_SERVICE = {
    name: "Service",
    duration: 60,
    price: 10,
    gallery: true,
    description: "",
    reschedule: true,
    enabled: true,
    position: null,
};

const ServicesList = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    // Main data state
    const [categories, setCategories] = useState<
        { category: Category; new: boolean }[]
    >([]);
    const [services, setServices] = useState<Record<string, Service[]>>({});

    // Loading state
    const [isLoading, setIsLoading] = useState(true);

    // Edit mode
    const [editMode, setEditMode] = useState(false);

    // For category deletion (modals)
    const [categoryForDeletion, setCategoryForDeletion] = useState<Category>();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // For easier navigation
    const [selectedCategoryTag, setSelectedCategoryTag] = useState("All");

    // For initial onboarding
    const [servicesConfigured, setServicesConfigured] = useState<boolean>(true);
    const [confirmOpen, setConfirmOpen] = useState(false);

    // For API requests
    const [cookies] = useCookies(["uuid", "brand_id", "theme_color"]);
    const { uuid, brand_id } = cookies;

    // To scroll down upon new category creation
    const listEndRef = useRef<null | HTMLDivElement>(null);
    const scrollDown = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    };

    // Scroll tracking for compact sticky header
    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        setScrolledPastHero(el.scrollTop > heroEl.offsetHeight - 80);
    }, []);

    // On filter change: scroll just enough to hide the hero
    useLayoutEffect(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        const target = heroEl.offsetHeight - 24;
        if (el.scrollTop < target) return;
        el.scrollTop = target;
    }, [selectedCategoryTag]);

    const navigate = useNavigate();

    useEffect(() => {
        if (!uuid || !brand_id) return;
        Brand.info(uuid, brand_id).then((b) => {
            setServicesConfigured(!!b?.setupSteps?.services);
        });
    }, [uuid, brand_id]);

    // Initial data fetching
    useEffect(() => {
        if (!uuid || !brand_id) {
            navigate("/");
            return;
        }
        if (!cookies.theme_color) window.location.reload();

        Category.list(uuid, brand_id, undefined, true).then((res) => {
            const sortedCategories =
                res?.map((x) => ({ category: x, new: false })) ?? [];

            setCategories(sortedCategories);

            if (sortedCategories.length === 0) {
                setIsLoading(false);
                return;
            }

            const servicePromises = sortedCategories.map((c) =>
                Service.list(
                    uuid,
                    brand_id,
                    c.category.key,
                    undefined,
                    true
                ).then((services) => ({
                    categoryKey: c.category.key,
                    services: services ?? [],
                }))
            );

            Promise.all(servicePromises).then((servicesByCategory) => {
                const serviceMap = Object.fromEntries(
                    servicesByCategory.map(({ categoryKey, services }) => [
                        categoryKey,
                        services,
                    ])
                );
                setServices(serviceMap);
                setIsLoading(false);
            });
        });
    }, [uuid, brand_id, navigate, cookies.theme_color]);

    const handleNextOrExit = () => {
        navigate("/");
    };

    const onAddSection = useCallback(() => {
        if (!uuid || !brand_id) return;

        const existingCategories = categories.map((x) => x.category.name);
        let newName = DEFAULT_CATEGORY.name;
        let count = 1;

        while (existingCategories.includes(newName)) {
            newName = `${DEFAULT_CATEGORY.name} ${count}`;
            count++;
        }

        const category = new Category(
            "",
            newName,
            DEFAULT_CATEGORY.enabled,
            null
        );
        Category.register(uuid, brand_id, category.name).then((res) => {
            if (!res) return;
            category.key = res;
            setCategories((x) => [...x, { category, new: true }]);
            setEditMode(true);
            scrollDown();
        });
    }, [uuid, brand_id, categories]);

    const moveCategory = (index: number, direction: "up" | "down") => {
        if (index < 0 || index >= categories.length) return;
        const newCategories = [...categories];

        const targetIndex = direction === "up" ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= categories.length) return;

        [newCategories[index], newCategories[targetIndex]] = [
            newCategories[targetIndex],
            newCategories[index],
        ];

        newCategories.forEach((item, i) => (item.category.position = i));
        setCategories(newCategories);
    };

    // Unified DnD (categories + cross-category services)
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );
    const [activeId, setActiveId] = useState<string | null>(null);

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(String(event.active.id));
    };

    const handleDragOver = (event: DragOverEvent) => {
        const { active, over } = event;
        if (!over) return;

        const activeStr = String(active.id);
        const overStr = String(over.id);

        // Only handle service cross-category movement
        if (!activeStr.startsWith("svc:")) return;

        setServices((prev) => {
            const activeKey = activeStr.replace("svc:", "");

            // Find source container
            let srcCat: string | null = null;
            for (const [catKey, list] of Object.entries(prev)) {
                if (list.some((s) => s.key === activeKey)) {
                    srcCat = catKey;
                    break;
                }
            }
            if (!srcCat) return prev;

            // Find target container
            let dstCat: string | null = null;
            let insertIdx = -1;

            if (overStr.startsWith("svc:")) {
                const overKey = overStr.replace("svc:", "");
                for (const [catKey, list] of Object.entries(prev)) {
                    const idx = list.findIndex((s) => s.key === overKey);
                    if (idx >= 0) {
                        dstCat = catKey;
                        insertIdx = idx;
                        break;
                    }
                }
            } else if (overStr.startsWith("drop:")) {
                dstCat = overStr.replace("drop:", "");
                insertIdx = prev[dstCat]?.length ?? 0;
            } else if (overStr.startsWith("cat:")) {
                dstCat = overStr.replace("cat:", "");
                insertIdx = prev[dstCat]?.length ?? 0;
            }

            if (!dstCat || srcCat === dstCat) return prev;

            // Move service between categories
            const src = [...(prev[srcCat] ?? [])];
            const dst = [...(prev[dstCat] ?? [])];

            const idx = src.findIndex((s) => s.key === activeKey);
            if (idx === -1) return prev;

            const [moved] = src.splice(idx, 1);
            moved.category = dstCat;
            dst.splice(insertIdx, 0, moved);

            src.forEach((s, i) => (s.position = i));
            dst.forEach((s, i) => (s.position = i));

            return { ...prev, [srcCat]: src, [dstCat]: dst };
        });
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveId(null);

        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const activeStr = String(active.id);
        const overStr = String(over.id);

        // Category reorder
        if (activeStr.startsWith("cat:") && overStr.startsWith("cat:")) {
            const activeKey = activeStr.replace("cat:", "");
            const overKey = overStr.replace("cat:", "");
            const oldIndex = categories.findIndex(
                (c) => c.category.key === activeKey
            );
            const newIndex = categories.findIndex(
                (c) => c.category.key === overKey
            );
            if (oldIndex === -1 || newIndex === -1) return;
            const newCategories = [...categories];
            const [moved] = newCategories.splice(oldIndex, 1);
            newCategories.splice(newIndex, 0, moved);
            newCategories.forEach((item, i) => (item.category.position = i));
            setCategories(newCategories);
            return;
        }

        // Service reorder within same category
        if (activeStr.startsWith("svc:") && overStr.startsWith("svc:")) {
            setServices((prev) => {
                const activeKey = activeStr.replace("svc:", "");
                const overKey = overStr.replace("svc:", "");

                let container: string | null = null;
                for (const [catKey, list] of Object.entries(prev)) {
                    if (list.some((s) => s.key === activeKey)) {
                        container = catKey;
                        break;
                    }
                }
                if (!container) return prev;

                // Check over is in same container (cross-container handled in onDragOver)
                const list = prev[container] ?? [];
                if (!list.some((s) => s.key === overKey)) return prev;

                const newList = [...list];
                const oldIdx = newList.findIndex((s) => s.key === activeKey);
                const newIdx = newList.findIndex((s) => s.key === overKey);
                if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx)
                    return prev;

                const [moved] = newList.splice(oldIdx, 1);
                newList.splice(newIdx, 0, moved);
                newList.forEach((s, i) => (s.position = i));

                return { ...prev, [container]: newList };
            });
        }
    };

    const handleDragCancel = () => {
        setActiveId(null);
    };

    // Active service for DragOverlay
    const activeServiceKey = activeId?.startsWith("svc:")
        ? activeId.replace("svc:", "")
        : null;
    const activeService = activeServiceKey
        ? Object.values(services)
              .flat()
              .find((s) => s.key === activeServiceKey)
        : null;

    const applyChanges = () => {
        setEditMode(false);

        const categoryUpdates = categories.map((item) =>
            Category.update(
                uuid,
                brand_id,
                item.category.key,
                item.category.name,
                item.category.enabled,
                item.category.position
            )
        );

        const serviceUpdates = Object.values(services)
            .flat()
            .map((service) =>
                Service.updatePosition(
                    uuid,
                    brand_id,
                    service.category,
                    service.key,
                    service.enabled,
                    service.position
                )
            );

        Promise.all([...categoryUpdates, ...serviceUpdates]).catch(
            console.error
        );
    };

    const onCategoryChange = useCallback(
        (category: Category) => {
            if (!uuid || !brand_id) return;
            const index = categories.findIndex(
                (x) => x.category.key === category.key
            );
            if (index >= 0) {
                const isNew = categories[index].new;
                Category.update(
                    uuid,
                    brand_id,
                    category.key,
                    category.name,
                    category.enabled,
                    category.position
                ).then(() => {
                    setCategories((prev) =>
                        prev.map((c, i) =>
                            i === index ? { category, new: false } : c
                        )
                    );

                    if (isNew) {
                        setEditMode(false);
                        scrollDown();
                    }
                });
            }
        },
        [uuid, brand_id, categories]
    );

    const onCategoryDelete = (category: Category) => {
        setCategoryForDeletion(category);
        setDeleteDialogOpen(true);
    };

    const onServicesConfigured = async () => {
        try {
            await Brand.updateServicesConfigured(uuid, brand_id);
            setServicesConfigured(true);
            setConfirmOpen(false);
            navigate("/");
        } finally {
        }
    };

    const onDeleteCategory = useCallback(
        async (category: Category) => {
            if (!uuid || !brand_id || !category) return;
            setIsDeleting(true);

            try {
                const success = await Category.delete(
                    uuid,
                    brand_id,
                    category.key
                );
                if (success) {
                    setCategories((prev) =>
                        prev.filter((c) => c.category.key !== category.key)
                    );
                    setDeleteDialogOpen(false);
                } else {
                    setDeleteDialogOpen(false);
                    setErrorMessage(
                        "Failed to delete the section. Make sure it does not contain services."
                    );
                    setErrorDialogOpen(true);
                }
            } catch (err) {
                console.error("Failed to delete section: ", err);
                setDeleteDialogOpen(false);
                setErrorMessage(
                    "An unexpected error occurred while trying to delete the section."
                );
                setErrorDialogOpen(true);
            } finally {
                setIsDeleting(false);
            }
        },
        [uuid, brand_id]
    );

    const onSelect = useCallback(
        (service: Service) =>
            navigate(`/menu/services/` + service.category + "/" + service.key),
        [navigate]
    );

    const onServiceAdd = useCallback(
        (categoryKey: string) => {
            let service = new Service({
                key: "",
                category: categoryKey,
                ...DEFAULT_SERVICE,
            });
            Service.register(
                uuid,
                brand_id,
                service.category,
                service.name,
                service.duration,
                service.price,
                service.gallery,
                service.description,
                service.reschedule,
                service.enabled
            ).then((res) => {
                if (!res) return;
                onSelect({ ...service, key: res });
            });
        },
        [uuid, brand_id, onSelect]
    );

    const onServiceChange = useCallback(
        (updatedService: Service) => {
            setServices((prev) => ({
                ...prev,
                [updatedService.category]:
                    prev[updatedService.category]?.map((s) =>
                        s.key === updatedService.key ? updatedService : s
                    ) ?? [],
            }));

            Service.update(
                uuid,
                brand_id,
                updatedService.category,
                updatedService.key,
                updatedService.name,
                updatedService.duration,
                updatedService.price,
                updatedService.gallery,
                updatedService.description,
                updatedService.reschedule,
                updatedService.enabled,
                updatedService.position,
                getPreferredLanguage(),
                updatedService.image
            );
        },
        [uuid, brand_id]
    );

    const currentLang = useCurrentLanguage();

    const categoryTags = [
        "All",
        ...categories
            .filter((c) => c.category.enabled || editMode)
            .map((c) => c.category.name),
    ];

    const categoryDisplayTags = [
        "All",
        ...categories
            .filter((c) => c.category.enabled || editMode)
            .map((c) =>
                pickLocalized(
                    c.category.name,
                    c.category.nameOriginal,
                    currentLang
                )
            ),
    ];

    const filtered = categories.filter((x) => {
        const matchesTag =
            selectedCategoryTag === "All" ||
            x.category.name === selectedCategoryTag;
        return (x.category.enabled || editMode) && matchesTag;
    });

    return (
        <>
            <Stack
                ref={scrollRef}
                onScroll={handleScroll}
                flex={1}
                sx={{ overflowY: "auto", overflowX: "hidden" }}
                bgcolor="white"
            >
                {/* ── Inline Hero ── */}
                <Stack
                    ref={heroRef}
                    width="100%"
                    sx={{
                        background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                        pt: "56px",
                        pb: "40px",
                        px: 2,
                        boxSizing: "border-box",
                        position: "relative",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            opacity: 0.04,
                            backgroundImage: NOISE_TEXTURE,
                            backgroundRepeat: "repeat",
                            backgroundSize: "128px 128px",
                            pointerEvents: "none",
                        },
                    }}
                >
                    {/* Back arrow */}
                    <ButtonBase
                        onClick={handleNextOrExit}
                        sx={{
                            position: "absolute",
                            top: 12,
                            left: 10,
                            zIndex: 2,
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "rgba(60,60,60,0.35)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <ColoredIcon
                            icon={BackIcon}
                            color="white"
                            size="28px"
                        />
                    </ButtonBase>

                    {/* Title + subtitle — glass card */}
                    <Stack
                        spacing={0.5}
                        sx={{
                            bgcolor: "rgba(255,255,255,0.18)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.3)",
                            borderRadius: "16px",
                            px: 2,
                            py: 1.5,
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="22px"
                            fontWeight={700}
                            color="white"
                        >
                            Services
                        </Typography>
                        {!servicesConfigured && (
                            <Typography
                                fontSize="13px"
                                color="rgba(255,255,255,0.7)"
                            >
                                Review the services generated for your business,
                                then confirm below.
                            </Typography>
                        )}
                    </Stack>

                    {/* Action pills */}
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        mt={2}
                        sx={{ position: "relative", zIndex: 1 }}
                    >
                        <ButtonBase
                            onClick={onAddSection}
                            sx={{
                                height: 38,
                                borderRadius: "19px",
                                px: 2,
                                bgcolor: "rgba(255,255,255,0.18)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                border: "1px solid rgba(255,255,255,0.3)",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                            }}
                        >
                            <AddRounded
                                sx={{ fontSize: 18, color: "white" }}
                            />
                            <Typography
                                fontSize="13px"
                                fontWeight={600}
                                color="white"
                            >
                                Add Section
                            </Typography>
                        </ButtonBase>

                        <ButtonBase
                            onClick={
                                editMode
                                    ? applyChanges
                                    : () => setEditMode(true)
                            }
                            sx={{
                                height: 38,
                                borderRadius: "19px",
                                px: 2,
                                bgcolor: editMode
                                    ? "white"
                                    : "rgba(255,255,255,0.18)",
                                backdropFilter: editMode
                                    ? undefined
                                    : "blur(16px)",
                                WebkitBackdropFilter: editMode
                                    ? undefined
                                    : "blur(16px)",
                                border: editMode
                                    ? undefined
                                    : "1px solid rgba(255,255,255,0.3)",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                transition:
                                    "background-color 0.2s ease, color 0.2s ease",
                            }}
                        >
                            <EditRounded
                                sx={{
                                    fontSize: 18,
                                    color: editMode ? primary : "white",
                                }}
                            />
                            <Typography
                                fontSize="13px"
                                fontWeight={600}
                                color={editMode ? primary : "white"}
                            >
                                {editMode ? "Apply Changes" : "Edit"}
                            </Typography>
                        </ButtonBase>
                    </Stack>
                </Stack>

                {/* ── Bottom Sheet ── */}
                <Stack
                    sx={{
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        mt: "-24px",
                        bgcolor: "white",
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    {/* ── Sticky Toolbar ── */}
                    <Box sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 4,
                        bgcolor: "white",
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                    }}>
                        {/* Compact header — slides in when hero scrolled off */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{
                                maxHeight: scrolledPastHero ? 52 : 0,
                                opacity: scrolledPastHero ? 1 : 0,
                                overflow: "hidden",
                                transition:
                                    "max-height 0.25s ease, opacity 0.2s ease",
                                px: 1,
                                borderBottom: scrolledPastHero
                                    ? "1px solid rgba(0,0,0,0.06)"
                                    : "none",
                            }}
                        >
                            <ButtonBase
                                onClick={handleNextOrExit}
                                sx={{ p: 0.5 }}
                            >
                                <ColoredIcon
                                    icon={BackIcon}
                                    color={primary}
                                    size="28px"
                                />
                            </ButtonBase>
                            <Typography
                                fontFamily={FONTS.display}
                                fontSize="16px"
                                fontWeight={600}
                                ml={0.5}
                            >
                                Services
                            </Typography>
                            <Box flex={1} />
                            <ButtonBase
                                onClick={onAddSection}
                                sx={{
                                    height: 32,
                                    borderRadius: "16px",
                                    px: 1.5,
                                    bgcolor: alpha(primary, 0.08),
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <AddRounded
                                    sx={{ fontSize: 16, color: primary }}
                                />
                                <Typography
                                    fontSize="12px"
                                    fontWeight={600}
                                    color={primary}
                                >
                                    Add Section
                                </Typography>
                            </ButtonBase>
                            <ButtonBase
                                onClick={
                                    editMode
                                        ? applyChanges
                                        : () => setEditMode(true)
                                }
                                sx={{
                                    height: 32,
                                    borderRadius: "16px",
                                    px: 1.5,
                                    ml: 0.75,
                                    bgcolor: editMode
                                        ? primary
                                        : alpha(primary, 0.08),
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    transition: "background-color 0.2s ease",
                                }}
                            >
                                <EditRounded
                                    sx={{
                                        fontSize: 16,
                                        color: editMode ? "white" : primary,
                                    }}
                                />
                                <Typography
                                    fontSize="12px"
                                    fontWeight={600}
                                    color={editMode ? "white" : primary}
                                >
                                    {editMode ? "Apply" : "Edit"}
                                </Typography>
                            </ButtonBase>
                        </Stack>

                        {/* Filter chips (view mode only, hidden while loading) */}
                        {!editMode && !isLoading && (
                            <Box
                                sx={{
                                    pt: scrolledPastHero ? 0.5 : 2,
                                    pb: 0.5,
                                    transition: "padding-top 0.25s ease",
                                }}
                            >
                                <TagFilterBar
                                    tags={categoryTags}
                                    displayTags={categoryDisplayTags}
                                    selectedTag={selectedCategoryTag}
                                    onSelectTag={setSelectedCategoryTag}
                                />
                            </Box>
                        )}

                        {/* Rounded-top spacer in edit mode when hero visible */}
                        {editMode && !scrolledPastHero && (
                            <Box sx={{ height: 12 }} />
                        )}
                    </Box>

                    {/* ── List Content ── */}
                    <Box>
                        {/* ── Content Area ── */}
                        <Stack
                            sx={{
                                bgcolor: "white",
                                pb: !editMode && !servicesConfigured
                                    ? "calc(4vh + 90px + 16vh)"
                                    : "calc(4vh + 90px)",
                                "@keyframes fadeSlideIn": {
                                    from: {
                                        opacity: 0,
                                        transform: "translateY(12px)",
                                    },
                                    to: { opacity: 1, transform: "translateY(0)" },
                                },
                                animation: isLoading ? undefined : "fadeSlideIn 0.3s ease-out",
                            }}
                        >
                            {/* Skeleton loading state */}
                            {isLoading ? (
                                <Stack sx={{ pt: 1 }}>
                                    {[0, 1].map((sectionIdx) => (
                                        <Stack key={sectionIdx}>
                                            {/* Section header skeleton */}
                                            <Box px={2} py={2}>
                                                <Skeleton
                                                    variant="rounded"
                                                    width={sectionIdx === 0 ? 120 : 90}
                                                    height={20}
                                                    sx={{ borderRadius: "6px" }}
                                                />
                                            </Box>
                                            {/* Service card skeletons */}
                                            {Array.from({ length: sectionIdx === 0 ? 3 : 2 }).map(
                                                (_, cardIdx) => (
                                                    <Stack
                                                        key={cardIdx}
                                                        direction="row"
                                                        spacing={1.5}
                                                        alignItems="center"
                                                        sx={{
                                                            ...CARD_STYLES,
                                                            p: 1.5,
                                                            mx: 2,
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        <Skeleton
                                                            variant="rounded"
                                                            width={64}
                                                            height={64}
                                                            sx={{
                                                                borderRadius: "14px",
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <Stack
                                                            spacing={0.75}
                                                            flex={1}
                                                        >
                                                            <Skeleton
                                                                variant="rounded"
                                                                width={`${55 + cardIdx * 15}%`}
                                                                height={16}
                                                                sx={{ borderRadius: "4px" }}
                                                            />
                                                            <Stack
                                                                direction="row"
                                                                spacing={1.5}
                                                            >
                                                                <Skeleton
                                                                    variant="rounded"
                                                                    width={40}
                                                                    height={14}
                                                                    sx={{ borderRadius: "4px" }}
                                                                />
                                                                <Skeleton
                                                                    variant="rounded"
                                                                    width={50}
                                                                    height={14}
                                                                    sx={{ borderRadius: "4px" }}
                                                                />
                                                            </Stack>
                                                        </Stack>
                                                    </Stack>
                                                )
                                            )}
                                        </Stack>
                                    ))}
                                </Stack>
                            ) : editMode ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragStart={handleDragStart}
                                    onDragOver={handleDragOver}
                                    onDragEnd={handleDragEnd}
                                    onDragCancel={handleDragCancel}
                                >
                                    <SortableContext
                                        items={filtered.map(
                                            (x) => "cat:" + x.category.key
                                        )}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {filtered.map((x, index) => (
                                            <SectionView
                                                key={x.category.key}
                                                uuid={uuid}
                                                brand_id={brand_id}
                                                category={x.category}
                                                new_item={x.new}
                                                onCategoryChange={onCategoryChange}
                                                onCategoryDelete={onCategoryDelete}
                                                editMode={editMode}
                                                onSelect={onSelect}
                                                onMoveUp={() =>
                                                    moveCategory(index, "up")
                                                }
                                                onMoveDown={() =>
                                                    moveCategory(index, "down")
                                                }
                                                index={index}
                                                total={categories.length}
                                                services={
                                                    services[x.category.key] ?? []
                                                }
                                                onServiceReorder={(newServices) =>
                                                    setServices((prev) => ({
                                                        ...prev,
                                                        [x.category.key]: newServices,
                                                    }))
                                                }
                                                onServiceAdd={() =>
                                                    onServiceAdd(x.category.key)
                                                }
                                                onServiceChange={onServiceChange}
                                            />
                                        ))}
                                    </SortableContext>
                                    <DragOverlay>
                                        {activeService && (
                                            <Stack
                                                direction="row"
                                                spacing={1.5}
                                                alignItems="center"
                                                sx={{
                                                    bgcolor: "white",
                                                    boxShadow:
                                                        "0 8px 24px rgba(0,0,0,0.15)",
                                                    borderRadius: "12px",
                                                    py: 1,
                                                    px: 1.5,
                                                }}
                                            >
                                                <Typography
                                                    fontSize="14px"
                                                    fontWeight={500}
                                                >
                                                    {activeService.name}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </DragOverlay>
                                </DndContext>
                            ) : (
                                filtered.map((x, index) => (
                                    <SectionView
                                        key={x.category.key}
                                        uuid={uuid}
                                        brand_id={brand_id}
                                        category={x.category}
                                        new_item={x.new}
                                        onCategoryChange={onCategoryChange}
                                        onCategoryDelete={onCategoryDelete}
                                        editMode={editMode}
                                        onSelect={onSelect}
                                        onMoveUp={() => moveCategory(index, "up")}
                                        onMoveDown={() => moveCategory(index, "down")}
                                        index={index}
                                        total={categories.length}
                                        services={services[x.category.key] ?? []}
                                        onServiceReorder={(newServices) =>
                                            setServices((prev) => ({
                                                ...prev,
                                                [x.category.key]: newServices,
                                            }))
                                        }
                                        onServiceAdd={() =>
                                            onServiceAdd(x.category.key)
                                        }
                                        onServiceChange={onServiceChange}
                                    />
                                ))
                            )}

                            {!isLoading && filtered.length === 0 && (
                                <EmptyListState primaryText="No matching services found" />
                            )}

                            <div ref={listEndRef} />
                            <DeleteDialog
                                open={deleteDialogOpen}
                                isDeleting={isDeleting}
                                entityName="section"
                                onClose={() => setDeleteDialogOpen(false)}
                                onDelete={() => onDeleteCategory(categoryForDeletion!)}
                            />
                            <ErrorDialog
                                open={errorDialogOpen}
                                errorMessage={errorMessage}
                                onClose={() => setErrorDialogOpen(false)}
                            />
                        </Stack>
                    </Box>
                </Stack>
            </Stack>

            {!editMode && servicesConfigured === false && (
                <BottomButton
                    onClick={() => setConfirmOpen(true)}
                    sticky
                    footer
                    noLoading
                >
                    I'm happy with my pricelist!
                </BottomButton>
            )}

            <Footer tab="admin" />
            <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <ConfirmModal
                    title="You are happy with the pricelist and ready to start showing it to clients"
                    cancelText="Not yet"
                    confirmText="Yes"
                    onCancel={() => setConfirmOpen(false)}
                    onConfirm={onServicesConfigured}
                />
            </Modal>
        </>
    );
};

export default ServicesList;
